import React, { useState } from "react";
import { useMentorFinance } from "@/hooks/useMentorFinance";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, PlusCircle, Plus, FileText, Users, Clock, Download, CreditCard, Banknote, AlertCircle, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const Financas = () => {
  const { 
    bankAccounts, 
    withdrawals, 
    financeSummary, 
    loading, 
    isProcessing,
    addBankAccount,
    requestWithdrawal,
    refreshData
  } = useMentorFinance();
  
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [showBankAccountDialog, setShowBankAccountDialog] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  
  // Form states
  const [bankAccountForm, setBankAccountForm] = useState({
    bank_name: "",
    holder_name: "",
    account_number: "",
    agency: "",
    pix_key: "",
    pix_key_type: "email" as 'cpf' | 'email' | 'telefone' | 'chave_aleatoria'
  });
  
  const [withdrawalForm, setWithdrawalForm] = useState({
    bank_account_id: "",
    amount: "",
  });
  
  const formatCurrency = (cents: number) => {
    return (cents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };
  
  const renderTransferType = (transferType: string) => {
    const icons = {
      pix: "🔐",
      ted: "🏦", 
      doc: "📄"
    };
    
    const labels = {
      pix: "PIX",
      ted: "TED",
      doc: "DOC"
    };
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs">
        {icons[transferType as keyof typeof icons]} {labels[transferType as keyof typeof labels]}
      </span>
    );
  };

  const handleAddBankAccount = async () => {
    const success = await addBankAccount(bankAccountForm);
    if (success) {
      setShowBankAccountDialog(false);
      setBankAccountForm({
        bank_name: "",
        holder_name: "",
        account_number: "",
        agency: "",
        pix_key: "",
        pix_key_type: "email"
      });
    }
  };

  const handleRequestWithdrawal = async () => {
    const amountCents = Math.round(parseFloat(withdrawalForm.amount) * 100);
    const success = await requestWithdrawal(withdrawalForm.bank_account_id, amountCents);
    if (success) {
      setShowWithdrawDialog(false);
      setWithdrawalForm({
        bank_account_id: "",
        amount: "",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-12 w-12 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 max-w-7xl space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-white">Finanças</h1>
          <p className="text-gray-400">Gerencie seus ganhos e saques</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => navigate('/painel-mentor/financas/detalhes-vendas')}
            variant="outline"
            className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
          >
            <Eye className="h-4 w-4 mr-2" />
            Ver Detalhes por Venda
          </Button>
        </div>
      </div>

      {/* Resumo Financeiro */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center space-y-2">
              <div className="text-sm text-gray-400">Total ganho</div>
              <div className="text-2xl font-bold text-green-400">
                {formatCurrency(financeSummary.totalEarned)}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center space-y-2">
              <div className="text-sm text-gray-400">Disponível para saque</div>
              <div className="text-2xl font-bold text-green-400">
                {formatCurrency(financeSummary.availableForWithdrawal)}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center space-y-2">
              <div className="text-sm text-gray-400">Saques pendentes</div>
              <div className="text-2xl font-bold text-yellow-400">
                {formatCurrency(financeSummary.pendingWithdrawals)}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex flex-col items-center space-y-2">
              <div className="text-sm text-gray-400">Aguardando liberação</div>
              <div className="text-2xl font-bold text-yellow-400">
                {formatCurrency(financeSummary.pendingReleases)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detalhamento por Fonte de Renda */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Mentorias */}
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5 text-[#00B6F0]" />
              Mentorias
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Total ganho:</span>
                <span className="text-green-400 font-semibold text-lg">
                  {formatCurrency((financeSummary.totalEarned || 0) - (financeSummary.digitalContentEarnings || 0))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Aguardando aluno liberar:</span>
                <span className="text-yellow-400 font-semibold text-lg">
                  {formatCurrency((financeSummary.pendingReleases || 0) - (financeSummary.digitalContentPendingRelease || 0))}
                </span>
              </div>
            </div>
            <div className="text-xs text-gray-500 bg-[#0F1419] p-4 rounded-lg">
              💡 <strong>Mentorias:</strong> Valores ficam disponíveis após o aluno liberar o pagamento
            </div>
          </CardContent>
        </Card>

        {/* Conteúdos Digitais */}
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#00B6F0]" />
              Conteúdos Digitais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Total ganho:</span>
                <span className="text-green-400 font-semibold text-lg">
                  {formatCurrency(financeSummary.digitalContentEarnings || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Disponível para saque:</span>
                <span className="text-green-400 font-semibold text-lg">
                  {formatCurrency(financeSummary.digitalContentAvailable || 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Aguardando 7 dias:</span>
                <span className="text-yellow-400 font-semibold text-lg">
                  {formatCurrency(financeSummary.digitalContentPendingRelease || 0)}
                </span>
              </div>
            </div>
            <div className="text-xs text-gray-500 bg-[#0F1419] p-4 rounded-lg">
              <Clock className="h-4 w-4 inline mr-1" />
              <strong>Liberação automática:</strong> Valores ficam disponíveis automaticamente após 7 dias da compra (lei de reembolso)
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de Saques e Contas */}
      <div className="space-y-6">
        <Tabs defaultValue="withdrawals" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-[#1E293B] border-[#2A3749] h-12">
            <TabsTrigger value="withdrawals" className="data-[state=active]:bg-[#00B6F0] data-[state=active]:text-black text-white">
              Saques
            </TabsTrigger>
            <TabsTrigger value="bank-accounts" className="data-[state=active]:bg-[#00B6F0] data-[state=active]:text-black text-white">
              Contas Bancárias
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="withdrawals" className="space-y-6 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Histórico de Saques</h2>
              <Button 
                onClick={() => setShowWithdrawDialog(true)}
                disabled={financeSummary.availableForWithdrawal <= 0 || bankAccounts.length === 0}
                className="bg-[#00B6F0] hover:bg-[#0095CC] text-black"
              >
                <Download className="h-4 w-4 mr-2" />
                Solicitar Saque
              </Button>
            </div>
            
            {withdrawals.length === 0 ? (
              <Card className="bg-[#1E293B] border-[#2A3749]">
                <CardContent className="p-8 text-center">
                  <p className="text-gray-400 text-lg">Nenhum saque realizado ainda</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {withdrawals.map((withdrawal) => (
                  <Card key={withdrawal.id} className="bg-[#1E293B] border-[#2A3749]">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-white text-lg">
                              {formatCurrency(withdrawal.amount_cents)}
                            </span>
                            {renderTransferType(withdrawal.transfer_type)}
                          </div>
                          <p className="text-sm text-gray-400">
                            Solicitado em {formatDate(withdrawal.requested_at)}
                          </p>
                          {withdrawal.bank_account && (
                            <p className="text-sm text-gray-500">
                              {withdrawal.bank_account.bank_name} - {withdrawal.bank_account.holder_name}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge 
                            variant={
                              withdrawal.status === 'approved' ? 'default' :
                              withdrawal.status === 'pending' ? 'secondary' :
                              withdrawal.status === 'rejected' ? 'destructive' : 'outline'
                            }
                            className={
                              withdrawal.status === 'approved' ? 'bg-green-600 text-white' :
                              withdrawal.status === 'pending' ? 'bg-yellow-600 text-white' :
                              withdrawal.status === 'rejected' ? 'bg-red-600 text-white' : ''
                            }
                          >
                            {withdrawal.status === 'approved' ? 'Aprovado' :
                             withdrawal.status === 'pending' ? 'Pendente' :
                             withdrawal.status === 'rejected' ? 'Rejeitado' : withdrawal.status}
                          </Badge>
                          {withdrawal.processed_at && (
                            <p className="text-xs text-gray-500 mt-2">
                              Processado em {formatDate(withdrawal.processed_at)}
                            </p>
                          )}
                        </div>
                      </div>
                      {withdrawal.admin_notes && (
                        <div className="mt-4 p-3 bg-[#0F1419] rounded-lg text-sm text-gray-400">
                          <strong>Observações:</strong> {withdrawal.admin_notes}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="bank-accounts" className="space-y-6 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">Contas Bancárias</h2>
              <Button 
                onClick={() => setShowBankAccountDialog(true)}
                className="bg-[#00B6F0] hover:bg-[#0095CC] text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Conta
              </Button>
            </div>
            
            {bankAccounts.length === 0 ? (
              <Card className="bg-[#1E293B] border-[#2A3749]">
                <CardContent className="p-8 text-center space-y-2">
                  <p className="text-gray-400 text-lg">Nenhuma conta bancária cadastrada</p>
                  <p className="text-sm text-gray-500">
                    Adicione uma conta para poder solicitar saques
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {bankAccounts.map((account) => (
                  <Card key={account.id} className="bg-[#1E293B] border-[#2A3749]">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <CreditCard className="h-5 w-5 text-[#00B6F0]" />
                            <span className="font-semibold text-white text-lg">
                              {account.bank_name}
                            </span>
                            {account.is_active && (
                              <Badge variant="outline" className="text-green-400 border-green-400">
                                Ativa
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">
                            {account.holder_name}
                          </p>
                          {account.pix_key && (
                            <p className="text-sm text-gray-500">
                              PIX: {account.pix_key} ({account.pix_key_type})
                            </p>
                          )}
                          {account.account_number && (
                            <p className="text-sm text-gray-500">
                              Ag: {account.agency} - CC: {account.account_number}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog para adicionar conta bancária */}
      <Dialog open={showBankAccountDialog} onOpenChange={setShowBankAccountDialog}>
        <DialogContent className="bg-[#1E293B] border-[#2A3749] text-white">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl">Adicionar Conta Bancária</DialogTitle>
            <DialogDescription className="text-gray-400">
              Adicione uma conta bancária ou chave PIX para receber seus pagamentos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="bank_name">Nome do Banco</Label>
              <Input
                id="bank_name"
                value={bankAccountForm.bank_name}
                onChange={(e) => setBankAccountForm(prev => ({ ...prev, bank_name: e.target.value }))}
                placeholder="Ex: Nubank, Banco do Brasil, PIX"
                className="bg-[#0F1419] border-[#2A3749] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="holder_name">Nome do Titular</Label>
              <Input
                id="holder_name"
                value={bankAccountForm.holder_name}
                onChange={(e) => setBankAccountForm(prev => ({ ...prev, holder_name: e.target.value }))}
                placeholder="Nome completo do titular da conta"
                className="bg-[#0F1419] border-[#2A3749] text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="agency">Agência</Label>
                <Input
                  id="agency"
                  value={bankAccountForm.agency}
                  onChange={(e) => setBankAccountForm(prev => ({ ...prev, agency: e.target.value }))}
                  placeholder="0000"
                  className="bg-[#0F1419] border-[#2A3749] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="account_number">Conta</Label>
                <Input
                  id="account_number"
                  value={bankAccountForm.account_number}
                  onChange={(e) => setBankAccountForm(prev => ({ ...prev, account_number: e.target.value }))}
                  placeholder="00000-0"
                  className="bg-[#0F1419] border-[#2A3749] text-white"
                />
              </div>
            </div>
            
            <div className="border-t border-[#2A3749] pt-6">
              <Label className="text-sm text-gray-400 mb-3 block">PIX (Opcional)</Label>
              
              <div className="grid grid-cols-3 gap-3">
                <Select
                  value={bankAccountForm.pix_key_type}
                  onValueChange={(value: 'cpf' | 'email' | 'telefone' | 'chave_aleatoria') => 
                    setBankAccountForm(prev => ({ ...prev, pix_key_type: value }))
                  }
                >
                  <SelectTrigger className="bg-[#0F1419] border-[#2A3749] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-[#2A3749]">
                    <SelectItem value="email">E-mail</SelectItem>
                    <SelectItem value="cpf">CPF</SelectItem>
                    <SelectItem value="telefone">Telefone</SelectItem>
                    <SelectItem value="chave_aleatoria">Chave Aleatória</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="col-span-2">
                  <Input
                    value={bankAccountForm.pix_key}
                    onChange={(e) => setBankAccountForm(prev => ({ ...prev, pix_key: e.target.value }))}
                    placeholder={
                      bankAccountForm.pix_key_type === 'email' ? 'seu@email.com' :
                      bankAccountForm.pix_key_type === 'cpf' ? '000.000.000-00' :
                      bankAccountForm.pix_key_type === 'telefone' ? '+5511999999999' :
                      'chave-aleatoria-uuid'
                    }
                    className="bg-[#0F1419] border-[#2A3749] text-white"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="pt-6 gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowBankAccountDialog(false)}
              className="border-[#2A3749] text-gray-400 hover:bg-[#2A3749]"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleAddBankAccount}
              disabled={isProcessing || !bankAccountForm.bank_name || !bankAccountForm.holder_name}
              className="bg-[#00B6F0] hover:bg-[#0095CC] text-black"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Conta'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para solicitar saque */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent className="bg-[#1E293B] border-[#2A3749] text-white">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl">Solicitar Saque</DialogTitle>
            <DialogDescription className="text-gray-400">
              Disponível para saque: {formatCurrency(financeSummary.availableForWithdrawal)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="bank_account">Conta Bancária</Label>
              <Select
                value={withdrawalForm.bank_account_id}
                onValueChange={(value) => setWithdrawalForm(prev => ({ ...prev, bank_account_id: value }))}
              >
                <SelectTrigger className="bg-[#0F1419] border-[#2A3749] text-white">
                  <SelectValue placeholder="Selecione uma conta" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-[#2A3749]">
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.bank_name} - {account.holder_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="amount">Valor (R$)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                max={financeSummary.availableForWithdrawal / 100}
                value={withdrawalForm.amount}
                onChange={(e) => setWithdrawalForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0,00"
                className="bg-[#0F1419] border-[#2A3749] text-white"
              />
            </div>
          </div>
          
          <DialogFooter className="pt-6 gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowWithdrawDialog(false)}
              className="border-[#2A3749] text-gray-400 hover:bg-[#2A3749]"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleRequestWithdrawal}
              disabled={
                isProcessing || 
                !withdrawalForm.bank_account_id || 
                !withdrawalForm.amount ||
                parseFloat(withdrawalForm.amount) <= 0 ||
                parseFloat(withdrawalForm.amount) > (financeSummary.availableForWithdrawal / 100)
              }
              className="bg-[#00B6F0] hover:bg-[#0095CC] text-black"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                'Solicitar Saque'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Financas;
